import * as React from 'react';
import styled from 'styled-components';
import RefreshIcon from '@material-ui/icons/Refresh';
import { HomeWorkOutlined, PeopleOutline } from '@material-ui/icons';

import {
  convertTableBoolean,
  convertTableUserRole,
} from '../../utils/convertTableValues';
import Table from '../../organisms/Table';
import ActionsPanelUsers, {
  SelectedAdditionalParams,
} from '../../organisms/ActionsPanelUsers';
import UsersForm from '../../forms/UsersForm';
import useTablePage from '../../hooks/useTablePage';
import DashboardLayout from '../../layouts/DashboardLayout';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import {
  createUser,
  updateUser,
  deleteUser,
  UserTableType,
  getUsers,
  resendInvitation,
} from '../../api/users';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import useUserRole from '../../hooks/useUserRole';
import UserPermitsModal from '../../organisms/UserPermitsModal';
import CompanyIndividualsModal from '../../organisms/CompanyIndividualsModal';
import { CLIENT_TYPES_VALUES, USER_TYPES_VALUES } from '../../constants/forms';

const StyledRefreshIcon = styled(RefreshIcon)`
  fill: #ff8c00;
`;

const UsersPage = () => {
  const {
    setSearch,
    handleAdd,
    search,
    tableManuallyUpdate,
    isModalOpen,
    values,
    handleEdit,
    handleDelete,
    errors,
    createOrUpdate,
    selectedItem,
    deleteById,
    handleCloseModal,
    handleCloseModalInfo,
    modalErrorTitle,
    setSelectedItem,
    modalError,
    modalTitle,
    modalSuccess,
    updateTable,
  } = useTablePage<UserTableType>({
    updateEndpoint: updateUser,
    deleteEndpoint: deleteUser,
    createEndpoint: createUser,
    modalCreateTitle: 'Add User',
    modalUpdateTitle: 'Update User',
  });

  const { isSuperAdmin } = useUserRole();

  const [isCreate, setIsCreate] = React.useState<boolean>(
    false,
  );

  React.useEffect(() => {
    if (modalTitle && modalTitle !== '') {
      if (modalTitle.toLowerCase().indexOf('add') !== -1) {
        setIsCreate(true);
      } else {
        setIsCreate(false);
      }
    }
  }, [modalTitle]);

  const [
    selectedAdditionalParams,
    setSelectedAdditionalParams,
  ] = React.useState<SelectedAdditionalParams>(undefined);

  const [isCompanyView, setIsCompanyView] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      selectedAdditionalParams &&
      selectedAdditionalParams.clientType === 2 &&
      selectedAdditionalParams.userRole === 3
    ) {
      setIsCompanyView(true);
    } else {
      setIsCompanyView(false);
    }
  }, [selectedAdditionalParams]);

  const columnsOrder = [
    'companyName',
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'userRole',
    'isActive',
  ];

  const columns = React.useCallback(
    column => {
      if (
        [
          'id',
          'isDeleted',
          'address',
          'city',
          'state',
          'street',
          'zipCode',
          'prNumber',
          'clientType',
          'licenses',
          'boLicense',
          'archEngLicense',
          'mobilePhoneNumber',
          'parentCompany',
          'tradesNames',
          'franchiseNames',
          'franchise',
          'trade',
          'avatar',
          'userRole',
          'isInvitationSent',
          'isEpsuperadmin',
          'createdAt',
          'canScheduleInspections',
          'defaultFranchiseId',
          'reportsAccessLevelId',
          'prLicense',
          'contactType',
          'isAccounting',
          'isInspection',
          isCompanyView ? null : 'companyName',
        ].includes(column)
      ) {
        return null;
      }

      return tableColumnsFormatter(column);
    },
    [isCompanyView],
  );

  // Table settings "Load all" sends `isDeleted: null`, which axios drops — and the
  // users endpoint reads a missing `is_deleted` as "Operational". Flag it explicitly
  // so deleted/inactive users show, as they do on the Employee Contacts page.
  const getUsersWithLoadAll = React.useCallback(
    params =>
      getUsers(
        params && params.isDeleted == null
          ? { ...params, loadAll: true }
          : params,
      ),
    [],
  );

  const formatValue = (value: any, column: string) =>
    convertTableBoolean(
      convertTableUserRole(value, column),
    );

  let isReuseFirstRequest = true;

  const [reuseModal, setReuseModal] = React.useState(false);

  const [permitUserId, setPermitUserId] = React.useState<number | null>(null);
  const [individualsCompany, setIndividualsCompany] = React.useState(null);

  // Same client actions the Employee Contacts page offers.
  const clientActions = row => {
    if (Number(row?.userRole) !== USER_TYPES_VALUES.CLIENT) return [];
    return [
      {
        title: 'View Permits',
        handler: () => setPermitUserId(row.id),
        Icon: HomeWorkOutlined,
      },
      Number(row?.clientType) === CLIENT_TYPES_VALUES.COMPANY && {
        title: 'View Individuals',
        handler: () => setIndividualsCompany(row),
        Icon: PeopleOutline,
      },
    ].filter(Boolean);
  };

  const additionalActions = row => [
    ...(row.isDeleted ? [] : [
      {
        title: 'Resend invitation',
        handler: () => {
          if (isReuseFirstRequest) {
            resendInvitation(row.id).then(() => {
              updateTable();
              setReuseModal(true);
              isReuseFirstRequest = false;
            });
          }
        },
        Icon: row.isInvitationSent
          ? RefreshIcon
          : StyledRefreshIcon,
      },
    ]),
    ...clientActions(row),
  ];

  const handleCloseReuseSuccessModalInfo = () => {
    setReuseModal(false);
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const onSubmit = (data:any) => {
    const _data = {
      ...data,
      defaultFranchiseId: data.defaultFranchise?.id,
    };
    createOrUpdate(_data);
  };
  return (
    <DashboardLayout>
      <PageWrapper>
        <ActionsPanelUsers
          onAdd={handleAdd}
          onSearch={setSearch}
          onSelectedAdditionalParamsChange={
            setSelectedAdditionalParams
          }
        />

      <Container>
        <Table
          customSearch
          searchQuery={search}
          columns={columns}
          rows={getUsersWithLoadAll}
          onEdit={handleEdit}
          onDelete={isSuperAdmin && handleDelete}
          columnsOrder={columnsOrder}
          formatValue={formatValue}
          manuallyUpdate={tableManuallyUpdate}
          additionalRequestParams={selectedAdditionalParams}
          additionalActions={additionalActions}
        />
      </Container>

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
          wide
        >
          <UsersForm
            initialValues={values}
            modalError={modalErrorTitle.length > 0}
            initialErrors={errors}
            onSubmit={onSubmit}
          />
        </ModalDefaultDraggable>
      )}

      {selectedItem && (
        <ModalDeleteConfirmation
          onCancel={() => setSelectedItem(null)}
          onConfirm={() => deleteById(selectedItem.id)}
        >
          <strong>{selectedItem.firstName}</strong>{' '}
          {selectedItem.lastName && (
            <strong>{selectedItem.lastName}</strong>
          )}{' '}
          / {selectedItem.email}
        </ModalDeleteConfirmation>
      )}

      {permitUserId && (
        <UserPermitsModal
          userId={permitUserId}
          onClose={() => setPermitUserId(null)}
        />
      )}

      {individualsCompany && (
        <CompanyIndividualsModal
          company={individualsCompany}
          onClose={() => setIndividualsCompany(null)}
        />
      )}

      {(modalSuccess || modalError || reuseModal) && (
        <ModalInfo
          title={
            isCreate
              ? 'Success! A message to set a password has been sent to your email.'
              : 'Success'
          }
          timeToClose={5000}
          isError={modalError}
          onClose={
            reuseModal
              ? handleCloseReuseSuccessModalInfo
              : handleCloseModalInfo
          }
          errorMsg={modalErrorTitle}
        />
      )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

export default UsersPage;
